// ARCHIVO COMPARTIDO app/web — editar en ambos repos. Verificar con `npm run check:shared` (TemControlApp).
//
// ── Hora del TRABAJADOR (zona horaria) ────────────────────────────────────
// Los eventos son INSTANTES UTC. Decidir a qué DÍA pertenece un instante
// (y a qué hora "de reloj" corresponde) depende de la zona horaria en la que
// se mire. Antes cada pantalla usaba la zona del DISPOSITIVO que miraba
// (getDate()/getDay()/getHours(), `new Date('YYYY-MM-DDT00:00')`…), así que
// el mismo trabajador tenía días distintos según el móvil/navegador: un
// teléfono en GMT mostraba 8 días de vacaciones en vez de 6 porque cada
// periodo (creado a las 00:00 de Madrid = 22:00/23:00 UTC del día anterior)
// empezaba un día antes.
//
// Decisión de producto: el día (y la hora de reloj) se deciden SIEMPRE en la
// zona del TRABAJADOR:
//   · 'Atlantic/Canary' si su comunidad/provincia es Canarias (Las Palmas,
//     Santa Cruz de Tenerife — subdivisión ES-CN).
//   · 'Europe/Madrid' en cualquier otro caso (y si no hay datos).
// Mismo resultado en cualquier dispositivo o navegador. Los periodos de día
// completo, ausencias y fichajes manuales también se CREAN en esa zona
// (zonedDateTimeToInstant).
//
// POR QUÉ NO USAMOS Intl
// Hermes en Android puede venir sin los datos de zonas de ICU completos, y
// `Intl.DateTimeFormat(..., { timeZone })` falla o ignora la zona. Como solo
// hay dos zonas posibles, implementamos la regla directamente:
//   · Horario de verano de la UE: desde el ÚLTIMO domingo de MARZO a las
//     01:00 UTC hasta el ÚLTIMO domingo de OCTUBRE a las 01:00 UTC (ambas
//     zonas cambian en el mismo instante UTC).
//   · Europe/Madrid   → UTC+1 (invierno) / UTC+2 (verano).
//   · Atlantic/Canary → UTC+0 (invierno) / UTC+1 (verano).
// VÁLIDO SOLO PARA ESTAS DOS ZONAS y para fechas desde 1996 (regla UE
// vigente). Si algún día la UE elimina el cambio de hora, hay que tocar
// isEuSummerTime. Los tests cruzan este cálculo con el Intl de Node (ICU
// completo) para cada hora de 2025-2027 en ambas zonas.
//
// Las "claves de día" son strings 'YYYY-MM-DD'. La aritmética de calendario
// sobre ellas (sumar días, día de la semana, rangos) es PURA: se hace con
// Date.UTC / getUTC*, nunca con la zona del dispositivo.

import { communityForProvince } from './spain';

export const TZ_MADRID = 'Europe/Madrid';
export const TZ_CANARY = 'Atlantic/Canary';
export const DEFAULT_TIME_ZONE = TZ_MADRID;
export const SUPPORTED_TIME_ZONES = [TZ_MADRID, TZ_CANARY];

// Texto de ayuda para la UI. Solo se enseña para trabajadores de Canarias
// (para Madrid es lo esperado y no aporta nada).
export const CANARY_TIME_LABEL = 'Hora de Canarias';

const MIN_MS = 60 * 1000;

// Offset en INVIERNO (hora estándar), en minutos respecto a UTC.
const STANDARD_OFFSET_MIN = { [TZ_MADRID]: 60, [TZ_CANARY]: 0 };

// Cualquier valor que no sea Canarias → Madrid (determinista, nunca la zona
// del dispositivo).
export const normalizeTimeZone = (tz) => (tz === TZ_CANARY ? TZ_CANARY : TZ_MADRID);

// ── Zona del trabajador ───────────────────────────────────────────────────
// `worker` es cualquier objeto con { autonomousCommunity, province } (el user
// del AuthContext o una fila de trabajador del backend). Se acepta también
// `community` como alias. Basta con que CUALQUIERA de los dos indique
// Canarias. La provincia se resuelve con el mapeo de src/lib/spain.js.
const CANARY_COMMUNITY_RE = /canari|^\s*es-cn\s*$/i;
const CANARY_PROVINCE_RE = /las\s+palmas|tenerife/i;

export const isCanaryWorker = (worker) => {
  if (!worker || typeof worker !== 'object') return false;
  const community = worker.autonomousCommunity ?? worker.community ?? null;
  if (community && CANARY_COMMUNITY_RE.test(String(community))) return true;
  const province = worker.province ?? null;
  if (!province) return false;
  const fromMap = communityForProvince(province);
  if (fromMap) return CANARY_COMMUNITY_RE.test(fromMap);
  return CANARY_PROVINCE_RE.test(String(province));
};

export const timeZoneForWorker = (worker) => (isCanaryWorker(worker) ? TZ_CANARY : TZ_MADRID);

// 'Hora de Canarias' para Canarias, null para Madrid (no se muestra nada).
export const timeZoneLabel = (tz) => (normalizeTimeZone(tz) === TZ_CANARY ? CANARY_TIME_LABEL : null);
export const timeZoneHintForWorker = (worker) => timeZoneLabel(timeZoneForWorker(worker));

// ── Instantes ─────────────────────────────────────────────────────────────
// Date | número (ms) | string ISO → ms (NaN si no es válido).
const toMs = (instant) => {
  if (instant == null || instant === '') return NaN;
  if (instant instanceof Date) return instant.getTime();
  if (typeof instant === 'number') return instant;
  return new Date(instant).getTime();
};

const DAY_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const pad2 = (n) => String(n).padStart(2, '0');
const keyFromUtcDate = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

// ── Horario de verano UE ──────────────────────────────────────────────────
// Último domingo de `month0` (0 = enero) de `year` a las 01:00 UTC, en ms.
const lastSundayAt1Utc = (year, month0) => {
  const lastDay = new Date(Date.UTC(year, month0 + 1, 0));
  return Date.UTC(year, month0, lastDay.getUTCDate() - lastDay.getUTCDay(), 1, 0, 0, 0);
};
const dstRangeCache = new Map(); // año UTC → [inicio, fin)
const dstRangeFor = (year) => {
  let r = dstRangeCache.get(year);
  if (!r) {
    r = [lastSundayAt1Utc(year, 2), lastSundayAt1Utc(year, 9)];
    dstRangeCache.set(year, r);
  }
  return r;
};

// ¿El instante cae en horario de verano de la UE? (Los cambios son en marzo
// y octubre, así que el año UTC del instante basta.)
export const isEuSummerTime = (instant) => {
  const ms = toMs(instant);
  if (!Number.isFinite(ms)) return false;
  const [start, end] = dstRangeFor(new Date(ms).getUTCFullYear());
  return ms >= start && ms < end;
};

// Offset (minutos a SUMAR a UTC para obtener la hora de reloj) en `tz` en ese
// instante. Madrid: 60 / 120. Canarias: 0 / 60. NaN si el instante no vale.
export const offsetMinutesAt = (instant, tz = DEFAULT_TIME_ZONE) => {
  const ms = toMs(instant);
  if (!Number.isFinite(ms)) return NaN;
  return STANDARD_OFFSET_MIN[normalizeTimeZone(tz)] + (isEuSummerTime(ms) ? 60 : 0);
};

// Componentes de reloj del instante en `tz`:
// { year, month (1-12), day, hour, minute, second, dow (0=Dom..6=Sáb), dayKey,
//   minutesOfDay }. null si el instante no vale.
export const zonedParts = (instant, tz = DEFAULT_TIME_ZONE) => {
  const ms = toMs(instant);
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms + offsetMinutesAt(ms, tz) * MIN_MS);
  const hour = d.getUTCHours();
  const minute = d.getUTCMinutes();
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour,
    minute,
    second: d.getUTCSeconds(),
    dow: d.getUTCDay(),
    dayKey: keyFromUtcDate(d),
    minutesOfDay: hour * 60 + minute,
  };
};

// Día ('YYYY-MM-DD') al que pertenece el instante en `tz`. Si recibe ya una
// clave de día ('YYYY-MM-DD' sin hora) la devuelve tal cual: una fecha pura
// no es un instante y no se "mueve" de zona. null si no vale.
export const dayKeyInZone = (instant, tz = DEFAULT_TIME_ZONE) => {
  if (typeof instant === 'string' && DAY_KEY_RE.test(instant)) return instant;
  const p = zonedParts(instant, tz);
  return p ? p.dayKey : null;
};

// Minutos desde las 00:00 (hora de reloj en `tz`). NaN si no vale.
export const minutesOfDayInZone = (instant, tz = DEFAULT_TIME_ZONE) => {
  const p = zonedParts(instant, tz);
  return p ? p.minutesOfDay : NaN;
};

// Día de la semana (0=Dom..6=Sáb) del instante en `tz`. NaN si no vale.
export const dowInZone = (instant, tz = DEFAULT_TIME_ZONE) => {
  const p = zonedParts(instant, tz);
  return p ? p.dow : NaN;
};

// 'HH:mm' de reloj en `tz` ('' si no vale). Para pintar horas de fichajes de
// un trabajador junto a sus días (si no, un fichaje de las 23:30 en Canarias
// saldría como "00:30" en un dispositivo de Madrid bajo el día anterior).
export const formatHmInZone = (instant, tz = DEFAULT_TIME_ZONE) => {
  const p = zonedParts(instant, tz);
  return p ? `${pad2(p.hour)}:${pad2(p.minute)}` : '';
};

// Hoy ('YYYY-MM-DD') en `tz`. `now` inyectable (tests); por defecto Date.now().
export const todayInZone = (tz = DEFAULT_TIME_ZONE, now = Date.now()) => dayKeyInZone(toMs(now), tz);

// ── Aritmética pura de claves de día ──────────────────────────────────────
// Solo fechas reales ('2026-02-30' o '2026-13-01' → null).
const parseDayKey = (key) => {
  const m = typeof key === 'string' ? DAY_KEY_RE.exec(key.slice(0, 10)) : null;
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return null;
  return { y, m: mo, d };
};

export const isDayKey = (key) => typeof key === 'string' && DAY_KEY_RE.test(key) && parseDayKey(key) != null;

// Suma `n` días (puede ser negativo) a una clave de día. null si no vale.
export const addDaysToDayKey = (key, n) => {
  const p = parseDayKey(key);
  if (!p) return null;
  return keyFromUtcDate(new Date(Date.UTC(p.y, p.m - 1, p.d + (Number(n) || 0))));
};

// Día de la semana (0=Dom..6=Sáb) de una clave de día. NaN si no vale.
export const dowOfDayKey = (key) => {
  const p = parseDayKey(key);
  if (!p) return NaN;
  return new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay();
};

// Todas las claves de día de [startKey, endKey] (inclusivo). [] si el rango
// no vale o está invertido.
export const dayKeysBetween = (startKey, endKey) => {
  const out = [];
  if (!isDayKey(startKey) || !isDayKey(endKey) || startKey > endKey) return out;
  let cur = startKey;
  while (cur <= endKey) {
    out.push(cur);
    cur = addDaysToDayKey(cur, 1);
  }
  return out;
};

// ── Hora de reloj en la zona → instante ───────────────────────────────────
// Convierte "este día a esta hora en la zona del trabajador" en el instante
// UTC real. Es lo que hay que usar al CREAR periodos / ausencias / fichajes
// manuales a partir de una fecha y una hora elegidas en la UI:
//   zonedDateTimeToInstant('2026-08-21', '00:00', 'Europe/Madrid')
//     → 2026-08-20T22:00:00.000Z
// `hm` acepta 'HH:mm' o 'HH:mm:ss' (por defecto '00:00').
// Casos del cambio de hora (mismo criterio que `new Date(y, m, d, h)`):
//   · Hora que NO existe (marzo, se salta una hora) → se interpreta con el
//     offset de invierno, es decir, cae una hora más tarde (02:30 → 03:30).
//   · Hora REPETIDA (octubre) → la PRIMERA ocurrencia (horario de verano).
// Devuelve un Date (Invalid Date si la fecha/hora no vale).
export const zonedDateTimeToInstant = (dayKey, hm = '00:00', tz = DEFAULT_TIME_ZONE) => {
  const p = parseDayKey(dayKey);
  const t = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(String(hm == null || hm === '' ? '00:00' : hm).trim());
  if (!p || !t) return new Date(NaN);
  const hh = Number(t[1]);
  const mm = Number(t[2]);
  const ss = Number(t[3] || 0);
  if (hh > 23 || mm > 59 || ss > 59) return new Date(NaN);
  const zone = normalizeTimeZone(tz);
  const wall = Date.UTC(p.y, p.m - 1, p.d, hh, mm, ss, 0);
  const std = STANDARD_OFFSET_MIN[zone];
  const asSummer = wall - (std + 60) * MIN_MS;
  const asWinter = wall - std * MIN_MS;
  const summerOk = isEuSummerTime(asSummer);
  const winterOk = !isEuSummerTime(asWinter);
  if (summerOk) return new Date(asSummer); // también la hora repetida (1ª ocurrencia)
  if (winterOk) return new Date(asWinter);
  return new Date(asWinter); // hueco de marzo
};

// Instante de las 00:00 de ese día en la zona.
export const startOfDayInZone = (dayKey, tz = DEFAULT_TIME_ZONE) => zonedDateTimeToInstant(dayKey, '00:00', tz);
